import type {CSSProperties} from "react";
import { useTranslation, Trans } from "react-i18next";
import ContentSection from "../../components/common/ContentSection/ContentSection";
import { logoUrl } from "../../utils/logoUrl";
import "./Partners.css";

// Real per-partner brand color (Municipalities.barva from the API) — not an
// invented palette, same principle as the accent tiles on the home page.
const PARTNERS = [
    { key: "brno", textId: "brno", accent: "#da2128" },
    { key: "most", textId: "most", accent: "#415BC7" }
] as const;

const OFFER_FEATURE_KEYS = ["anyStructure", "editing", "visualization"] as const;

const Partners = () => {
    const { t } = useTranslation("partners");
    const { t: tOffer } = useTranslation("offer");

    return (
        <div className="page-sections">
            <ContentSection title={t('ourPartners')} level={1} contentClassName="partners-content">
                {PARTNERS.map((partner, index) => (
                    <div
                        key={partner.key}
                        className={`partner-row${index % 2 === 1 ? " partner-row--reversed" : ""}`}
                    >
                        <a
                            href={t(`links.${partner.key}`)}
                            target="_blank"
                            rel="noreferrer"
                            className="partner-logo"
                            style={{"--tile-accent": partner.accent} as CSSProperties}
                        >
                            <img src={logoUrl(partner.textId)} alt={t(`${partner.key}.heading`)} />
                        </a>
                        <div className="partner-text">
                            <h3>{t(`${partner.key}.heading`)}</h3>
                            <p>
                                <Trans
                                    ns="partners"
                                    i18nKey={`${partner.key}.text`}
                                    components={{
                                        thesis: <a href={t('links.thesis')} target="_blank" rel="noreferrer" />
                                    }}
                                />
                            </p>
                        </div>
                    </div>
                ))}
            </ContentSection>

            <ContentSection title={t('joinHeading')} level={1}>
                <p>
                    <Trans
                        ns="partners"
                        i18nKey="cooperationCall"
                        components={{
                            email: <a href={t('links.email')} />,
                            nkod: <a href={t('links.nkod')} target="_blank" rel="noreferrer" />
                        }}
                    />
                </p>
            </ContentSection>

            <ContentSection title={tOffer("heading")} level={1}>
                <p>{tOffer("intro")}</p>
            </ContentSection>

            <ContentSection title={tOffer("Flow.heading")} subtitle={tOffer("Flow.subheading")} level={1}>
                <div className="offer-flow">
                    <div className="offer-flow-step">
                        <span className="offer-flow-icon"><i className="pi pi-database" /></span>
                        <h3>{tOffer("Flow.input.title")}</h3>
                        <p>{tOffer("Flow.input.text")}</p>
                    </div>

                    <div className="offer-flow-arrow" aria-hidden="true"><i className="pi pi-arrow-right" /></div>

                    <div className="offer-flow-step">
                        <span className="offer-flow-icon"><i className="pi pi-sync" /></span>
                        <h3>{tOffer("Flow.convert.title")}</h3>
                        <p>{tOffer("Flow.convert.text")}</p>
                    </div>

                    <div className="offer-flow-arrow" aria-hidden="true"><i className="pi pi-arrow-right" /></div>

                    <div className="offer-flow-branch">
                        <div className="offer-flow-step">
                            <span className="offer-flow-icon"><i className="pi pi-user-edit" /></span>
                            <h3>{tOffer("Flow.manage.title")}</h3>
                            <p>{tOffer("Flow.manage.text")}</p>
                        </div>
                        <div className="offer-flow-step">
                            <span className="offer-flow-icon"><i className="pi pi-chart-bar" /></span>
                            <h3>{tOffer("Flow.public.title")}</h3>
                            <p>{tOffer("Flow.public.text")}</p>
                        </div>
                    </div>
                </div>
            </ContentSection>

            <ContentSection title={tOffer("Features.heading")} level={1} contentClassName="offer-feature-grid">
                {OFFER_FEATURE_KEYS.map((key) => (
                    <div className="offer-feature" key={key}>
                        <h3>{tOffer(`Features.${key}.title`)}</h3>
                        <p>{tOffer(`Features.${key}.text`)}</p>
                    </div>
                ))}
            </ContentSection>
        </div>
    )
}

export {Partners}
